/**
 * Excel Export Helpers
 *
 * Utility functions for post-processing AG Grid Excel exports.
 * Uses ExcelJS to export, add additional formatting, and combine sheets into a single auto-fitted sheet.
 *
 * Cell-format DEFINITION (`CELL_FORMAT_CONFIG`) and grid APPLICATION (`formatCellValue`) live in
 * `formatResolver.ts` — the single shared format module. This file owns Excel sheet assembly only
 * (merges, borders, spacers, numFmt application, auto-fit, download) and imports the format config.
 */

import { CellFormat, PageScaleValues } from '@services/api/deckManagement/graphql/schema/API';
import { excelFormatFor, resolveCellFormat } from '@utils/formatResolver';
import ExcelJS from 'exceljs';

/**
 * Format inputs for one source sheet / PageLayout section in an Excel export.
 * Arrays are ordered by the section's emitted data columns and numbered data rows.
 */
export interface ExcelExportFormatSection {
  columnKeys: string[];
  columnDefaults: (CellFormat | null)[];
  rowDefaults: (CellFormat | null)[];
  cellOverrides?: Array<Record<string, CellFormat | null> | null>;
  /** The page's declared unit, folded into the numeric formats. See `excelFormatFor`. */
  pageScale?: PageScaleValues | null;
}
const colLetterToNum = (letters: string): number => {
  let num = 0;
  for (let i = 0; i < letters.length; i += 1) {
    num = num * 26 + (letters.charCodeAt(i) - 64);
  }
  return num;
};

/** Parses a merge reference (e.g. "C5:F5") into numeric row/col ranges */
const parseMerge = (merge: string) => {
  const [start, end] = merge.split(':');
  return {
    startCol: colLetterToNum(start.replace(/\d+/g, '')),
    endCol: colLetterToNum(end.replace(/\d+/g, '')),
    startRow: parseInt(start.replace(/[A-Z]+/gi, ''), 10),
    endRow: parseInt(end.replace(/[A-Z]+/gi, ''), 10),
  };
};

/** Returns true if the cell in column 1 contains a numeric row number */
const isNumberedRow = (row: ExcelJS.Row): boolean => typeof row.getCell(1).value === 'number';

/** Returns true when a row carries no text, number, formula, or rich-text cell values. */
const isEmptyRow = (row: ExcelJS.Row, columnCount: number): boolean => {
  for (let columnNumber = 1; columnNumber <= columnCount; columnNumber += 1) {
    const value = row.getCell(columnNumber).value;
    if (value !== null && value !== undefined && value !== '') return false;
  }
  return true;
};

/** Returns true when a header row has at least one data-plane value beyond the pinned columns. */
const hasDataHeaderValues = (row: ExcelJS.Row, columnCount: number): boolean => {
  for (let columnNumber = 3; columnNumber <= columnCount; columnNumber += 1) {
    const value = row.getCell(columnNumber).value;
    if (value !== null && value !== undefined && value !== '') return true;
  }
  return false;
};

/**
 * Finds an empty grid-header padding row introduced when the unified pinned title stack is deeper
 * than the data-side header hierarchy. It is safe to remove only when it sits between two real
 * data-header rows; blank section separators before data rows do not match this shape.
 */
const findPaddedHeaderRows = (sheet: ExcelJS.Worksheet): Set<number> => {
  const firstNumberedRow = Array.from({ length: sheet.rowCount }, (_, index) => index + 1).find((rowNumber) =>
    isNumberedRow(sheet.getRow(rowNumber)),
  );
  if (!firstNumberedRow) return new Set();

  const paddedRows = new Set<number>();
  // Rows 1-4 are caller-owned prependContent (blank/title/unit/letters); start at grid headers.
  for (let rowNumber = 5; rowNumber < firstNumberedRow; rowNumber += 1) {
    const row = sheet.getRow(rowNumber);
    const previousRow = sheet.getRow(rowNumber - 1);
    const nextRow = sheet.getRow(rowNumber + 1);
    if (
      isEmptyRow(row, sheet.columnCount) &&
      !isNumberedRow(previousRow) &&
      !isNumberedRow(nextRow) &&
      hasDataHeaderValues(previousRow, sheet.columnCount) &&
      hasDataHeaderValues(nextRow, sheet.columnCount)
    ) {
      paddedRows.add(rowNumber);
    }
  }
  return paddedRows;
};

const legacyBlackBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FF000000' } };

/** Returns whether a border side matches the medium-black legacy workbook profile. */
const isMediumBlackBorder = (border: Partial<ExcelJS.Border> | undefined): boolean =>
  border?.style === 'medium' && border.color?.argb?.toUpperCase() === 'FF000000';

/**
 * Restores header and total-row styling that was previously applied by the legacy export path.
 * AG Grid provides the source values and most styling, but omits the group-header frames,
 * scenario baseline, and vertical sides of framed totals after sheet post-processing.
 *
 * Per-section aware: each section frames only ITS OWN columns using ITS OWN spacer set + extent, so a
 * narrower section (e.g. Total Summary §2 with 3 blocks vs §1's 4) does not frame the phantom blocks
 * that only exist on the wider section's part of the shared column axis.
 */
const applyLegacyExcelStyleProfile = (
  sheet: ExcelJS.Worksheet,
  copiedSheetRowCounts: number[],
  spacerPositionsBySection: Set<number>[],
  sectionLastColumns: number[],
): void => {
  let sectionStartRow = 1;

  copiedSheetRowCounts.forEach((copiedRowCount, sectionIndex) => {
    const groupHeaderRow = sheet.getRow(sectionStartRow + 4);
    const scenarioHeaderRow = sheet.getRow(sectionStartRow + 5);
    const leafHeaderRow = sheet.getRow(sectionStartRow + 6);
    const sectionSpacers = spacerPositionsBySection[sectionIndex] ?? new Set<number>();
    // Only style up to this section's own last column — never the wider section's phantom columns.
    const lastColumn = sectionLastColumns[sectionIndex] ?? sheet.columnCount;

    for (let columnNumber = 3; columnNumber <= lastColumn; columnNumber += 1) {
      if (sectionSpacers.has(columnNumber)) continue;
      const cell = groupHeaderRow.getCell(columnNumber);
      cell.border = {
        top: legacyBlackBorder,
        right: legacyBlackBorder,
        bottom: legacyBlackBorder,
        left: legacyBlackBorder,
      };
    }

    for (let columnNumber = 3; columnNumber <= lastColumn; columnNumber += 1) {
      if (sectionSpacers.has(columnNumber)) continue;
      const cell = scenarioHeaderRow.getCell(columnNumber);
      cell.border = { ...cell.border, bottom: legacyBlackBorder };
      cell.alignment = {
        ...cell.alignment,
        horizontal: 'center',
        vertical: 'bottom',
        wrapText: true,
      };
    }

    // Leaf labels wrap like the scenario band above them.
    for (let columnNumber = 3; columnNumber <= lastColumn; columnNumber += 1) {
      if (sectionSpacers.has(columnNumber)) continue;
      const cell = leafHeaderRow.getCell(columnNumber);
      cell.alignment = { ...cell.alignment, wrapText: true };
    }

    sectionStartRow += copiedRowCount;
  });

  // Per-section output-row ranges so framed-total detection can bound each numbered row to ITS OWN
  // section's column extent + spacer set — not the full sheet width / union spacers. Otherwise a
  // narrower section's total rows would include the wider section's phantom (empty, border-less)
  // columns, which fail isMediumBlackBorder and wrongly disqualify the row from framing.
  const sectionRanges: { startRow: number; lastColumn: number; spacers: Set<number> }[] = [];
  let rangeStart = 1;
  copiedSheetRowCounts.forEach((count, idx) => {
    sectionRanges.push({
      startRow: rangeStart,
      lastColumn: sectionLastColumns[idx] ?? sheet.columnCount,
      spacers: spacerPositionsBySection[idx] ?? new Set<number>(),
    });
    rangeStart += count;
  });
  const sectionForRow = (rowNumber: number): (typeof sectionRanges)[number] | undefined =>
    sectionRanges.find((range, idx) => {
      const next = sectionRanges[idx + 1];
      return rowNumber >= range.startRow && (!next || rowNumber < next.startRow);
    });

  sheet.eachRow({ includeEmpty: true }, (row) => {
    if (!isNumberedRow(row)) return;

    // Scope the row's data cells to its own section's width + spacers (see sectionRanges above).
    const section = sectionForRow(row.number);
    const lastColumn = section?.lastColumn ?? sheet.columnCount;
    const sectionSpacers = section?.spacers ?? new Set<number>();

    const dataCells: ExcelJS.Cell[] = [];
    for (let columnNumber = 2; columnNumber <= lastColumn; columnNumber += 1) {
      if (!sectionSpacers.has(columnNumber)) dataCells.push(row.getCell(columnNumber));
    }

    const isFramedTotalRow =
      dataCells.some((cell) => cell.font?.bold) &&
      dataCells.every((cell) => isMediumBlackBorder(cell.border?.top) && isMediumBlackBorder(cell.border?.bottom));
    if (!isFramedTotalRow) return;

    for (const cell of dataCells) {
      cell.border = { ...cell.border, left: legacyBlackBorder, right: legacyBlackBorder };
    }
  });
};

/**
 * Applies Excel numFmt to a numbered row using the shared cell > explicit-row > column cascade.
 * The column arrays are in source-grid order; colMap shifts each source data column around spacers.
 */
const addCellNumFormats = (
  row: ExcelJS.Row,
  dataRowIndex: number,
  formatSection: ExcelExportFormatSection | undefined,
  colMap: (sourceColumn: number) => number,
): void => {
  if (!formatSection) return;

  const rowDefault = formatSection.rowDefaults[dataRowIndex] ?? null;
  const cellOverrides = formatSection.cellOverrides?.[dataRowIndex] ?? null;

  formatSection.columnKeys.forEach((columnKey, columnIndex) => {
    const effectiveFormat = resolveCellFormat(
      formatSection.columnDefaults[columnIndex] ?? null,
      rowDefault,
      cellOverrides?.[columnKey] ?? null,
    );
    const targetRow = row;
    targetRow.getCell(colMap(columnIndex + 3)).numFmt = excelFormatFor(effectiveFormat, formatSection.pageScale);
  });
};

/**
 * Processes an AG Grid Excel blob into a single sheet with auto-fitted column widths.
 * For multi-section pages, sheets are stacked vertically. For single-section pages,
 * the sheet is preserved as-is with column widths adjusted to content.
 *
 * @param blob - The Excel blob from AG Grid's getMultipleSheetsAsExcel()
 * @param sheetName - The name for the output sheet
 * @param blockSizes - Array of column counts per block (e.g. [4, 3, 4, 6]) for spacer insertion
 * @param formatSections - Optional per-sheet column, row, and cell-format metadata for effective numFmt resolution
 * @returns A new Blob with combined content and auto-fitted columns
 */
export const processExcelExport = async (
  blob: Blob,
  sheetName: string,
  blockSizesPerSection: number[][] = [],
  formatSections: ExcelExportFormatSection[] = [],
): Promise<Blob> => {
  const arrayBuffer = await blob.arrayBuffer();

  // Read the AG Grid workbook
  const sourceWorkbook = new ExcelJS.Workbook();
  await sourceWorkbook.xlsx.load(arrayBuffer);

  // Create a new workbook with a single combined sheet for export
  const outputWorkbook = new ExcelJS.Workbook();
  const outputSheet = outputWorkbook.addWorksheet(sheetName);

  // Compute spacer column positions (in output space) PER SECTION. Sections can have different block
  // structures (e.g. Total Summary §1 [5,5,4,5] vs §2 [5,5,4]), so each section owns its own spacer
  // set. The OUTPUT column axis is the UNION of all sections' spacers (an Excel sheet has one shared
  // column axis); a narrower section keeps the union's extra spacer columns present but simply does
  // not draw its own block frame/borders there.
  const blockSizesToSpacerSet = (blockSizes: number[]): Set<number> => {
    const positions = new Set<number>();
    if (blockSizes.length > 1) {
      let col = 2; // first 2 cols are row number + KPI name
      for (let i = 0; i < blockSizes.length - 1; i += 1) {
        col += blockSizes[i] + 1; // +1 for the spacer column itself
        positions.add(col);
      }
    }
    return positions;
  };

  const spacerPositionsBySection: Set<number>[] = blockSizesPerSection.map(blockSizesToSpacerSet);
  // Union of every section's spacer positions = the shared output column axis.
  // Union of every section's spacer positions = the shared output column axis. Built SORTED ASCENDING:
  // colMap below accumulates its offset by walking these in order (`if (srcCol + offset >= spacerCol)
  // offset += 1`), which is only correct for ascending iteration. A Set preserves INSERTION order, and
  // this union is assembled in section order — so when an earlier section has wider leading blocks than
  // a later one, the raw insertion order is not ascending and colMap would return wrong columns. Sorting
  // makes it order-independent of section shapes.
  const spacerPositions = new Set<number>(spacerPositionsBySection.flatMap((set) => [...set]).sort((a, b) => a - b));

  // Map source column to output column, shifting data columns past the union spacer positions.
  const colMap = (srcCol: number): number => {
    if (spacerPositions.size === 0 || srcCol <= 2) return srcCol;
    let offset = 0;
    spacerPositions.forEach((spacerCol) => {
      if (srcCol + offset >= spacerCol) offset += 1;
    });
    return srcCol + offset;
  };

  // Each section's last physical (output) column on the shared union axis. A section's last SOURCE
  // data column is 2 leading cols + its own total leaves; colMap shifts that past any union spacers.
  // Used to stop per-section header framing at the section's own width (no phantom wider-section cols).
  const sectionLastColumns: number[] = blockSizesPerSection.map((blockSizes) => {
    const totalLeaves = blockSizes.reduce((sum, count) => sum + count, 0);
    return totalLeaves > 0 ? colMap(2 + totalLeaves) : 2;
  });

  // For tracking row positioning across sheets
  let currentRow = 1;
  const copiedSheetRowCounts: number[] = [];
  let sourceSheetIndex = 0;
  // Stack sheets vertically by copying rows and adjusting merged cell references
  sourceWorkbook.eachSheet((sourceSheet) => {
    const paddedHeaderRows = findPaddedHeaderRows(sourceSheet);
    const sourceToOutputRows = new Map<number, number>();
    const formatSection = formatSections[sourceSheetIndex];
    let dataRowIndex = 0;

    // Copy rows from source sheet into the combined output, skipping only redundant core-only
    // header padding rows. Their surrounding data-header rows remain adjacent after the copy.
    sourceSheet.eachRow({ includeEmpty: true }, (row, sourceRowNumber) => {
      if (paddedHeaderRows.has(sourceRowNumber)) return;
      sourceToOutputRows.set(sourceRowNumber, currentRow);
      const outputRow = outputSheet.getRow(currentRow);

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const outputCell = outputRow.getCell(colMap(colNumber));
        outputCell.value = cell.value;

        // Preserve styling
        if (cell.font) outputCell.font = { ...cell.font };
        if (cell.alignment) outputCell.alignment = { ...cell.alignment };
        if (cell.fill && cell.fill.type) outputCell.fill = { ...cell.fill } as ExcelJS.Fill;
        if (cell.border) outputCell.border = { ...cell.border };
        if (cell.numFmt) outputCell.numFmt = cell.numFmt;
      });

      if (isNumberedRow(row)) {
        addCellNumFormats(outputRow, dataRowIndex, formatSection, colMap);
        dataRowIndex += 1;
      }
      if (row.height) outputRow.height = row.height;
      outputRow.commit();
      currentRow += 1;
    });
    copiedSheetRowCounts.push(sourceToOutputRows.size);

    // Reposition merged cells through the source-to-output row map. Merges belonging to a skipped
    // core-only padding row are intentionally omitted; all subsequent header/data merges shift up.
    sourceSheet.model.merges?.forEach((merge: string) => {
      const { startCol, endCol, startRow, endRow } = parseMerge(merge);
      const mergeTouchesSkippedRow = Array.from({ length: endRow - startRow + 1 }, (_, index) => startRow + index).some(
        (rowNumber) => paddedHeaderRows.has(rowNumber),
      );
      if (mergeTouchesSkippedRow) return;

      const outputStartRow = sourceToOutputRows.get(startRow);
      const outputEndRow = sourceToOutputRows.get(endRow);
      if (outputStartRow === undefined || outputEndRow === undefined) return;
      outputSheet.mergeCells(outputStartRow, colMap(startCol), outputEndRow, colMap(endCol));
    });
    sourceSheetIndex += 1;
  });

  // Merge granularity cell vertically down 1 row per section
  // Row 2 of each section contains the granularity label (already merged A:B horizontally)
  // Extend it to also span row 3 vertically (which AGGrid export could not support alone)
  let sectionStartRow = 1;
  let sectionIndex = 0;
  sourceWorkbook.eachSheet((sourceSheet) => {
    const granularityRow = sectionStartRow + 1;
    try {
      outputSheet.unMergeCells(`A${granularityRow}:B${granularityRow}`);
      outputSheet.mergeCells(`A${granularityRow}:B${granularityRow + 1}`);
    } catch {
      // Skip if cells are not in expected merge state
    }
    sectionStartRow += copiedSheetRowCounts[sectionIndex] ?? sourceSheet.rowCount;
    sectionIndex += 1;
  });

  // Find the first row with a row number; the data area includes section headers before it.
  let firstNumberedRow = 0;
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (isNumberedRow(row) && !firstNumberedRow) firstNumberedRow = rowNumber;
  });
  // Data area starts 2 rows before first numbered row (section header + blank separator)
  const dataAreaStart = Math.max(firstNumberedRow - 2, 1);

  // Per-section output-row ranges (start row + block-header row) so spacer styling keys off the
  // section a row belongs to — not a hardcoded global "row 5". Each section's block-header row is its
  // start + 4 (matching applyLegacyExcelStyleProfile's offset).
  const sectionRanges: { startRow: number; blockHeaderRow: number; spacers: Set<number> }[] = [];
  let rangeStart = 1;
  copiedSheetRowCounts.forEach((count, idx) => {
    sectionRanges.push({
      startRow: rangeStart,
      blockHeaderRow: rangeStart + 4,
      spacers: spacerPositionsBySection[idx] ?? new Set<number>(),
    });
    rangeStart += count;
  });
  const blockHeaderRows = new Set<number>(sectionRanges.map((r) => r.blockHeaderRow));
  const sectionForRow = (rowNumber: number): (typeof sectionRanges)[number] | undefined =>
    sectionRanges.find((r, idx) => {
      const next = sectionRanges[idx + 1];
      return rowNumber >= r.startRow && (!next || rowNumber < next.startRow);
    });

  // Set spacer column widths (from the shared union axis) and apply borders to suppress gridlines.
  // The grey block-header separator border is drawn only on a spacer that is REAL for the section
  // that row belongs to — a narrower section leaves union spacers it doesn't own as plain white.
  const whiteBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFFFFFFF' } };
  const greyBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFC0BFC0' } };
  spacerPositions.forEach((pos) => {
    outputSheet.getColumn(pos).width = 2.6;
    for (let r = 1; r <= outputSheet.rowCount; r += 1) {
      const cell = outputSheet.getRow(r).getCell(pos);
      const section = sectionForRow(r);
      const isOwnSpacer = section?.spacers.has(pos) ?? false;
      if (blockHeaderRows.has(r) && isOwnSpacer) {
        // Block header row of this section — grey left/right borders as a visible block separator.
        cell.border = { left: greyBorder, right: greyBorder, bottom: whiteBorder };
      } else if (r < dataAreaStart) {
        // Header rows — white borders on all sides to suppress gridlines
        cell.border = { left: whiteBorder, right: whiteBorder, bottom: whiteBorder };
      } else {
        // Data area (numbered + section headers + blank separators) — only white bottom
        cell.border = { bottom: whiteBorder };
      }
    }
  });

  const whiteTopBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFFFFFFF' } };

  // Apply white fill to suppress gridlines on non-data rows (headers, blank separators)
  // Rows with a row number in col 1 and footnote rows retain gridlines
  const whiteFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
  let lastNumberedRow = 0;
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (isNumberedRow(row)) lastNumberedRow = rowNumber;
  });

  // Suppress top gridline on first footnote row
  const footnoteRow = outputSheet.getRow(lastNumberedRow + 2);
  if (footnoteRow) {
    const cell = footnoteRow.getCell(2);
    if (!cell.border?.top) {
      cell.border = { ...cell.border, top: whiteTopBorder };
    }
  }

  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (isNumberedRow(row)) return;
    if (rowNumber > lastNumberedRow + 1) return; // footnotes start after blank line
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      // Col 2: skip white fill if row has KPI content or is adjacent to numbered data rows
      if (colNumber === 2 && rowNumber >= dataAreaStart) {
        if (row.getCell(2).value) return; // section header with text
        const prevHasNumber = rowNumber > 1 && isNumberedRow(outputSheet.getRow(rowNumber - 1));
        const nextHasNumber = rowNumber < outputSheet.rowCount && isNumberedRow(outputSheet.getRow(rowNumber + 1));
        if (prevHasNumber || nextHasNumber) return;
      }
      if (spacerPositions.has(colNumber)) return;
      if (!cell.fill || cell.fill.type !== 'pattern' || cell.fill.pattern === 'none') {
        outputSheet.getRow(rowNumber).getCell(colNumber).fill = whiteFill;
      }
    });
  });

  // Add grey bottom border on numbered data rows (row separator matching reference deck)
  // Skip last row in each section (followed by non-numbered row or blank separator)
  const greyBottomBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FF808080' } };
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (!isNumberedRow(row)) return;
    const nextRow = outputSheet.getRow(rowNumber + 1);
    if (!isNumberedRow(nextRow)) return;
    if (!nextRow.getCell(2).value) return; // blank separator — skip
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 1 || spacerPositions.has(colNumber)) return;
      if (!cell.border?.bottom) {
        outputSheet.getRow(rowNumber).getCell(colNumber).border = { ...cell.border, bottom: greyBottomBorder };
      }
    });
  });

  // Add white top border on first numbered row of each section (only if it received a grey bottom border)
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (!isNumberedRow(row)) return;
    const prevRow = rowNumber > 1 ? outputSheet.getRow(rowNumber - 1) : null;
    if (!prevRow || isNumberedRow(prevRow)) return;
    const cell = row.getCell(2);
    if (cell.border?.bottom && !cell.border?.top) {
      cell.border = { ...cell.border, top: whiteTopBorder };
    }
  });

  applyLegacyExcelStyleProfile(outputSheet, copiedSheetRowCounts, spacerPositionsBySection, sectionLastColumns);

  // Auto-fit column widths based on cell content.
  // Two exclusions from the width calc:
  //  - cells in a wide merge (>5 cols): major report headers / footnotes that would otherwise force a
  //    data column absurdly wide.
  //  - ANY merged cell in the pinned columns (row-number A, KPI-name B): the granularity/title label is
  //    merged across A:B (e.g. "Summary" in A2), and since that merge spans only 2 cols it is not a
  //    "wide" merge — but its text must not size column A, which should track only the 1-3 digit row
  //    numbers. Without this, a long title (Total Summary's "Summary", 7 chars) widens the row-number
  //    column while a short one (FX Summary's "MTD", 3 chars) happens not to.
  const excludedWidthCellAddresses = new Set<string>();
  outputSheet.model.merges?.forEach((merge: string) => {
    const { startCol, endCol, startRow, endRow } = parseMerge(merge);
    const isWideMerge = endCol - startCol > 5;
    const touchesPinnedColumns = startCol <= 2;
    if (!isWideMerge && !touchesPinnedColumns) return;
    for (let r = startRow; r <= endRow; r += 1) {
      for (let c = startCol; c <= endCol; c += 1) {
        excludedWidthCellAddresses.add(`${r}:${c}`);
      }
    }
  });

  // Width units per character scaling for Arial size 30 (calibrated from Excel: ~2.75 units/char)
  const charWidthFactor = 2.75;

  outputSheet.columns.forEach((_column, colIdx) => {
    const colNum = colIdx + 1;
    if (spacerPositions.has(colNum)) return;
    let maxCellWidth = 0;
    const currColumn = outputSheet.getColumn(colNum);
    currColumn.eachCell?.({ includeEmpty: true }, (cell, rowNumber) => {
      if (excludedWidthCellAddresses.has(`${rowNumber}:${colNum}`)) return;
      const cellLength = cell.value ? cell.value.toString().length : 0;
      maxCellWidth = Math.max(maxCellWidth, cellLength);
    });
    if (colIdx < 2) {
      // Allows for Row number and KPI name columns — auto-fit to content
      currColumn.width = maxCellWidth < 4 ? 10 : Math.ceil(maxCellWidth * charWidthFactor);
    } else {
      // Data columns — fixed width matching scenario column expectations
      currColumn.width = 33;
    }
  });

  // Generate output blob to be used in export
  const outputBuffer = await outputWorkbook.xlsx.writeBuffer();
  return new Blob([outputBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Triggers a browser file download from a Blob
 *
 * @param blob - The file blob to download
 * @param fileName - The desired file name
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
